/**
 * Where a cabinet run's DRAG HANDLES sit in an elevation.
 *
 * The plan answers this in world metres on a canvas (`wallHandlesUtils` +
 * `bayWidthDrag`); an elevation draws into its own pre-zoom pixel space
 * (`x2D` / `y2D`), so the geometry cannot be shared — only the SIZES are, from
 * `grabHandleUtils`, or the two views grow different-sized targets for the same
 * gesture. Same split, and same reason, as `elevationGrabHandle`.
 *
 * PURE: no React, no SVG, no run state. What it does NOT do is decide which
 * handles to offer — that is the scope's business (`useCabinetEditScope`), and
 * the caller's.
 *
 * ## The one hard part: a param is not an x
 *
 * A bay's own edges need no conversion — the renderer already knows the box it
 * drew, so its edges are `x2D ± renderedWidth / 2`. A RUN END does: it is a
 * param along a wall, and nothing drawn is at it.
 *
 * The mapping is affine and recoverable from anything already placed:
 * `x = x0 + sign · p · pxPerMetre`, where `sign` is the same measured value
 * `bayDragFrame` resolves (the elevation's screen-right and the run's own wall
 * direction are parallel but NOT always same-signed). Given one reference box
 * whose param is known, `x0` falls out. Exact, not approximate — which matters,
 * because a handle that sits a few pixels off the thing it moves jumps on the
 * first drag frame.
 */
import {
  GRAB_HANDLE_RADIUS_PX,
  GRAB_HANDLE_HIT_RADIUS_PX,
} from './grabHandleUtils';

/**
 * Turn a param along the run's wall into an elevation x, from one box whose
 * param is already known.
 *
 * @param {{x2D:number}} refBox        a drawn box on this wall
 * @param {number} refParam            that box's param along the wall
 * @param {1|-1} sign                  `bayDragFrame().sign` — measured, never assumed
 * @param {number} pxPerMetre          the elevation's pre-zoom scale
 * @returns {((p:number)=>number)|null}
 */
export function paramToElevationX(refBox, refParam, sign, pxPerMetre) {
  if (!refBox || !Number.isFinite(refBox.x2D)) return null;
  if (!Number.isFinite(refParam) || !(pxPerMetre > 0)) return null;
  if (sign !== 1 && sign !== -1) return null;
  const x0 = refBox.x2D - sign * refParam * pxPerMetre;
  return (p) => (Number.isFinite(p) ? x0 + sign * p * pxPerMetre : null);
}

/**
 * A handle's horizontal reach, clipped so it cannot cross into a neighbour's.
 *
 * The same rule `bayWidthHandles` applies between a bay's two edges, shared
 * because a SHORT run puts its two end handles just as close together — and
 * where two positioned elements' hit areas overlap, the DOM picks by stacking
 * order rather than by distance.
 *
 * Halfway to the nearest neighbour on each side, and never more than the full
 * reach. With no neighbour in a direction the reach is simply the full one.
 *
 * There is deliberately NO floor holding the area at least as big as the
 * visible disc. One was tried and removed: for handles closer together than
 * two radii it re-introduced the overlap this exists to prevent, and an
 * overlap is a CORRECTNESS failure (the DOM then picks by stacking order, so
 * the press lands on the wrong handle) where a disc wider than its own target
 * is merely cosmetic. If the two are ever that close, the discs visibly
 * overlap anyway and the drawing is already telling the user so.
 */
function splitReach(h, all) {
  const reachTo = (dir) => {
    const gaps = all
      .filter((o) => o !== h && Math.sign(o.x - h.x) === dir)
      .map((o) => Math.abs(o.x - h.x) / 2);
    const nearest = gaps.length ? Math.min(...gaps) : Infinity;
    return Math.min(h.hitRadius, nearest);
  };
  return { left: reachTo(-1), right: reachTo(1) };
}

/** Screen-sized constants live in SCREEN px, so they divide by the host's zoom. */
const sized = (zoom) => ({
  radius: GRAB_HANDLE_RADIUS_PX / zoom,
  hitRadius: GRAB_HANDLE_HIT_RADIUS_PX / zoom,
});

/**
 * A BAY's two width handles: one at each vertical mid-height edge of the box.
 *
 * Mid-height rather than a shared line across the wall, so the handle is
 * unambiguously part of the cabinet it resizes — an upper's handle on the
 * counter line would sit nowhere near the upper, and the elevation's whole
 * value is that you are looking straight at the face whose width you are
 * setting.
 *
 * Both edges, always. The plan yields neither (see `bayEdgeHandles`), and here
 * there is no run-end handle to collide with in the same scope either.
 *
 * @param {{x2D:number, y2D:number, renderedWidth:number}} box
 * @param {number} zoom
 */
export function bayWidthHandles(box, zoom = 1, state = {}) {
  const { x2D, y2D, renderedWidth } = box || {};
  if (![x2D, y2D, renderedWidth].every(Number.isFinite)) return [];
  if (!(zoom > 0) || renderedWidth <= 0) return [];
  const { radius, hitRadius } = sized(zoom);
  const half = renderedWidth / 2;
  // NEAREST-WINS, EXPRESSED AS GEOMETRY.
  //
  // Each handle is its own positioned element, so the DOM decides what a press
  // hits — and where two hit areas overlap it picks by stacking order, which is
  // the first-match-wins defect the plan's hit test had to fix in code. A 12"
  // bay is ~30 px against a 26 px reach, so they WOULD overlap.
  //
  // So the reach is clipped INWARD at the bay's midpoint while staying generous
  // outward (grabbing just past a narrow bay still works). The two areas then
  // meet without crossing, and the browser's own hit test resolves exactly as
  // nearest-wins would.
  const inward = Math.min(hitRadius, half);
  return [
    {
      key: 'bay-width:start',
      kind: 'bay-width',
      side: 'start',
      x: x2D - half,
      hit: { left: hitRadius, right: inward },
    },
    {
      key: 'bay-width:end',
      kind: 'bay-width',
      side: 'end',
      x: x2D + half,
      hit: { left: inward, right: hitRadius },
    },
  ].map((h) => ({
    ...h,
    y: y2D,
    radius,
    hitRadius,
    // Both edges of a hard bay are pinned together: the reason is about the
    // BAY (what stands in it sizes it), never about one side of it.
    pinned: !!state.pinned,
    reason: state.reason || null,
  }));
}

/**
 * The RUN's end handles, placed by param.
 *
 * An end that falls outside the drawn wall is DROPPED rather than clamped to
 * the edge: a run can turn a corner, and the other arm's end belongs to a
 * different elevation. A handle pinned to the margin would claim to move
 * something the reader cannot see, and dragging it would be measured against a
 * wall this drawing is not of.
 *
 * @param {Array<{end:'start'|'end', param:number}>} ends
 * @param {(p:number)=>number} xForParam  from `paramToElevationX`
 * @param {{y:number, minX:number, maxX:number}} band
 * @param {number} zoom
 */
export function runEndHandles(ends, xForParam, band, zoom = 1) {
  if (!ends?.length || !xForParam || !band || !(zoom > 0)) return [];
  const { radius, hitRadius } = sized(zoom);
  return ends
    .map((e) => {
      const x = xForParam(e.param);
      if (!Number.isFinite(x)) return null;
      // Inclusive of the margin a handle's own radius needs, so one sitting
      // exactly at the wall's end is still fully drawn.
      if (x < band.minX - radius || x > band.maxX + radius) return null;
      return {
        key: `run-end:${e.end}`,
        kind: 'run-end',
        side: e.end,
        // Where along the wall it was placed. The host anchors the drag
        // frame here, so a handle without it drags from the wall's start.
        param: e.param,
        x,
        y: band.y,
        radius,
        hitRadius,
        pinned: !!e.pinned,
        reason: e.reason || null,
      };
    })
    .filter(Boolean)
    .map((h, _i, all) => ({ ...h, hit: splitReach(h, all) }));
}

/**
 * The ISLAND's move disc — the free-standing leg's own grab point.
 *
 * In the plan this drags in two dimensions. An elevation has ONE axis, so the
 * disc here slides the leg along its own direction: a strict subset of the same
 * edit, through the same `moveFreeSegment`, and the only part of it this
 * drawing can express. Placed at the leg's midpoint so it does not sit over
 * either end handle.
 */
export function legMoveHandle(midParam, xForParam, band, zoom = 1) {
  if (!xForParam || !band || !(zoom > 0)) return null;
  const x = xForParam(midParam);
  if (!Number.isFinite(x)) return null;
  if (x < band.minX || x > band.maxX) return null;
  const { radius, hitRadius } = sized(zoom);
  return {
    key: 'leg',
    kind: 'leg',
    side: null,
    // The drag's anchor, as on the run ends above.
    param: midParam,
    x,
    y: band.y,
    radius,
    hitRadius,
    // The only handle on its row, so there is nothing to yield to.
    hit: { left: hitRadius, right: hitRadius },
  };
}
